import React, { useCallback, useEffect, useRef, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { telefonoBonito } from "../../../utils/telefonoBonito";
import { ProspectCatalogs, ProspectRow } from "../../../services/v2/prospects.service";
import PromoBloqueModal from "./promoBloqueModal";
import {
	cuando, errorTexto, ETAPAS, etapaClase, nombreCompleto, OBJETIVOS_BARRA, resumenWapp, statusClase, vencido
} from "./salesHelpers";

const PER_PAGE = 250;

interface Props {
	campusId: number;
	catalogs: ProspectCatalogs | null;
	owner: string;
	onOwner: (o: string) => void;
	onOpen: (id: number) => void;
	onSalir: () => void;
}

const FLAGS: { key: string; label: string }[] = [
	{ key: "sin_nota_30", label: "Sin nota 30 d" }, { key: "vencidas", label: "Actividad vencida" }, { key: "califican_promo", label: "Califica a promo" },
];

const ETAPA_CORTA: { [k: number]: string } = { 1: "Pre-insc", 2: "Matric", 3: "Retirado", 4: "Graduado" };

/** Selector de cartera (lienzo): una asesora, su equipo entero, sin asesora o todo el plantel. */
export const CarteraSelect: React.FC<{ catalogs: ProspectCatalogs | null; value: string; onChange: (v: string) => void }> = ({ catalogs, value, onChange }) => {
	const porEquipo: { [t: string]: { id: string; name: string }[] } = {};
	for (const a of catalogs?.advisors || []) (porEquipo[a.team || "Otras"] = porEquipo[a.team || "Otras"] || []).push(a);
	return (
		<select value={value} onChange={(e) => onChange(e.target.value)} aria-label="Cartera">
			<option value="me">Mi cartera</option>
			<option value="all">Todo el plantel</option>
			<option value="none">Sin asesora asignada</option>
			{Object.keys(porEquipo).sort().map((t) => (
				<optgroup key={t} label={t}>
					<option value={"team:" + t}>Equipo {t} completo</option>
					{porEquipo[t].map((a) => <option key={a.id} value={"z:" + a.id}>{a.name}</option>)}
				</optgroup>
			))}
		</select>
	);
};

/**
 * Cartera de la asesora (lienzo): toda su gente en una tabla, con filtros, conteos y acciones en bloque.
 * Supervisión elige la asesora o el equipo; una asesora ve solo la suya.
 */
const CarteraView: React.FC<Props> = ({ campusId, catalogs, owner, onOwner, onOpen, onSalir }) => {
	const http = HttpManager.getInstance();
	const sup = !!catalogs?.isSupervisor;
	const [etapa, setEtapa] = useState<number | null>(null);
	const [status, setStatus] = useState<number | null>(null);
	const [objetivo, setObjetivo] = useState<string>("");
	const [curso, setCurso] = useState<string>("");
	const [cursoQ, setCursoQ] = useState<string>("");
	const [search, setSearch] = useState<string>("");
	const [q, setQ] = useState<string>("");
	const [flags, setFlags] = useState<string[]>([]);
	const [rows, setRows] = useState<ProspectRow[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [page, setPage] = useState<number>(1);
	const [kpis, setKpis] = useState<{ total: number; etapas: { [k: string]: number }; sin_tocar_30: number } | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [marcados, setMarcados] = useState<number[]>([]);
	const [reasignarA, setReasignarA] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [promo, setPromo] = useState<boolean>(false);

	const filtros = { owner, etapa, status: status ? [status] : undefined, objetivo: objetivo || null, curso: cursoQ || null, q: q || undefined, flags };

	const turno = useRef<number>(0);
	const cargar = useCallback(async (p: number) => {
		const yo = ++turno.current;
		setLoading(true); setError(null);
		try {
			const [l, k] = await Promise.all([
				http.prospectsService.list(campusId, filtros, PER_PAGE, p),
				p === 1 ? http.prospectsService.cartera(campusId, { ...filtros, etapa: null }) : Promise.resolve(null),
			]);
			if (yo !== turno.current) return;
			setRows(l.data.data); setTotal(l.data.total); setPage(p);
			if (k) setKpis(k.data);
		} catch (e) { if (yo === turno.current) setError(errorTexto(e)); }
		finally { if (yo === turno.current) setLoading(false); }
	}, [campusId, owner, etapa, status, objetivo, cursoQ, q, flags.join(",")]);

	useEffect(() => { cargar(1); setMarcados([]); }, [cargar]);

	const reasignar = async () => {
		if (!reasignarA || !marcados.length) return;
		setBusy(true); setError(null);
		try {
			const r = await http.prospectsService.assignBulk(campusId, marcados, reasignarA, null);
			setMarcados([]); setReasignarA("");
			if (r.data.fallas.length) setError(`${r.data.reasignados} reasignados · ${r.data.fallas.length} no se pudieron`);
			cargar(page);
		} catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};
	const excel = async () => {
		try {
			const r = await http.prospectsService.export(campusId, filtros);
			const url = URL.createObjectURL(r.data);
			const a = document.createElement("a"); a.href = url; a.download = "cartera.csv"; a.click();
			setTimeout(() => URL.revokeObjectURL(url), 2000);
		} catch (e) { setError(errorTexto(e)); }
	};

	const desde = total ? (page - 1) * PER_PAGE + 1 : 0;
	const hasta = Math.min(total, page * PER_PAGE);
	const n = (v: number | undefined) => (v || 0).toLocaleString("es-MX");

	return (
		<div className="ct">
			<div className="ct-cab">
				<div className="ct-quien">
					<h2>Cartera</h2>
					{sup ? <CarteraSelect catalogs={catalogs} value={owner} onChange={onOwner} /> : <span className="apagado">Mi cartera</span>}
				</div>
				<div className="tb-herr">
					<button className="enlace" onClick={onSalir}>← Volver a la bandeja</button>
					{sup && marcados.length ? (
						<>
							<span className="apagado">{marcados.length} marcados</span>
							<select value={reasignarA} onChange={(e) => setReasignarA(e.target.value)} aria-label="Reasignar a">
								<option value="">Reasignar a…</option>
								{(catalogs?.advisors || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
							</select>
							<button className="btn-wa pri" disabled={!reasignarA || busy} onClick={reasignar}>Reasignar</button>
							<button className="btn-wa" onClick={() => setMarcados([])}>Quitar</button>
						</>
					) : null}
					<button className="btn-wa" onClick={excel}>Excel</button>
					{sup ? <button className="btn-wa pri" onClick={() => setPromo(true)}>Enviar promo a los que aplican</button> : null}
				</div>
			</div>

			<div className="ct-cuerpo">
				<div className="tb-kpis">
					<div className="tb-kpi"><span>Total</span><b>{n(kpis?.total)}</b></div>
					{ETAPAS.slice(0, 3).map((e) => <div key={e.id} className="tb-kpi"><span>{e.name}</span><b>{n(kpis?.etapas?.[e.id])}</b></div>)}
					<div className="tb-kpi"><span>Sin tocar 30 d</span><b className={(kpis?.sin_tocar_30 || 0) > 0 ? "oro" : ""}>{n(kpis?.sin_tocar_30)}</b></div>
				</div>

				<div className="ct-filtros">
					<input type="search" placeholder="Buscar" value={search} aria-label="Buscar" onChange={(e) => setSearch(e.target.value)}
						onKeyDown={(e) => { if (e.key === "Enter") setQ(search.trim()); if (e.key === "Escape") { setSearch(""); setQ(""); } }} />
					<select value={etapa || ""} onChange={(e) => setEtapa(e.target.value ? Number(e.target.value) : null)} aria-label="Etapa">
						<option value="">Todas las etapas</option>
						{ETAPAS.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
					</select>
					<select value={status || ""} onChange={(e) => setStatus(e.target.value ? Number(e.target.value) : null)} aria-label="Status">
						<option value="">Todos los status</option>
						{(catalogs?.statuses || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
					</select>
					<select value={objetivo} onChange={(e) => setObjetivo(e.target.value)} aria-label="Objetivo">
						<option value="">Todos los objetivos</option>
						{OBJETIVOS_BARRA.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
					</select>
					<input type="search" placeholder="Curso" value={curso} aria-label="Curso" onChange={(e) => setCurso(e.target.value)}
						onKeyDown={(e) => { if (e.key === "Enter") setCursoQ(curso.trim()); }} onBlur={() => setCursoQ(curso.trim())} style={{ width: 140 }} />
					{FLAGS.map((f) => (
						<label key={f.key} className={"ct-flag" + (flags.includes(f.key) ? " on" : "")}>
							<input type="checkbox" aria-label={f.label} checked={flags.includes(f.key)} onChange={(e) => setFlags(e.target.checked ? [...flags, f.key] : flags.filter((x) => x !== f.key))} />
							{f.label}
						</label>
					))}
				</div>

				{error ? <div className="aviso error">{error}</div> : null}

				<div className="ct-tabla-caja" style={loading ? { opacity: .5 } : undefined}>
					<table className="tb-tabla ct-tabla">
						<thead>
							<tr>
								{sup ? <th style={{ width: 28 }}><input type="checkbox" aria-label="Marcar todos" checked={!!rows.length && marcados.length === rows.length}
									onChange={(e) => setMarcados(e.target.checked ? rows.map((r) => r.id) : [])} /></th> : null}
								<th>Cliente</th><th>Etapa</th><th>Status</th><th>Objetivo</th><th>Curso</th><th>Bitácora</th><th>Último toque</th><th>Próxima actividad</th>
							</tr>
						</thead>
						<tbody>
							{rows.map((r) => {
								const resumen = resumenWapp(r.wapp);
								return (
									<tr key={r.id} className={marcados.includes(r.id) ? "marcada" : ""}>
										{sup ? <td><input type="checkbox" aria-label={"Marcar a " + nombreCompleto(r)} checked={marcados.includes(r.id)}
											onChange={(e) => setMarcados(e.target.checked ? [...marcados, r.id] : marcados.filter((x) => x !== r.id))} /></td> : null}
										<td>
											<button className="ct-nombre" onClick={() => onOpen(r.id)}>{nombreCompleto(r)}</button>
											<div className="apagado ct-sub">{r.phone_e164 ? telefonoBonito(r.phone_e164) : ""}{owner !== "me" && r.owner_zoho_name ? " · " + r.owner_zoho_name : ""}</div>
											{resumen ? <div className="ct-sub ct-resumen">{resumen}</div> : null}
										</td>
										<td><span className={"chip " + etapaClase(r.etapa_id)}>{ETAPA_CORTA[r.etapa_id]}</span></td>
										<td><span className={"chip " + statusClase(r.status_id)}>{r.status}</span></td>
										<td>{(r.objetivos || []).join(" · ") || <span className="apagado">—</span>}</td>
										<td>{r.wapp?.curso || r.course_interest_text || <span className="apagado">—</span>}</td>
										<td>{r.events_count}</td>
										<td>{cuando(r.last_activity_at)}</td>
										<td>{r.next_action_at ? <span className={vencido(r.next_action_at) ? "oro" : ""}>{vencido(r.next_action_at) ? "vencida · " : ""}{cuando(r.next_action_at)}</span> : <span className="apagado">—</span>}</td>
									</tr>
								);
							})}
						</tbody>
					</table>
					{!loading && !rows.length ? <div className="ventas-vacio">Nadie con estos filtros.</div> : null}
				</div>

				<div className="ventas-pag">
					<span>{n(desde)} – {n(hasta)} de {n(total)} · {PER_PAGE} por página</span>
					<span style={{ display: "flex", gap: 6 }}>
						<button disabled={page <= 1} onClick={() => cargar(page - 1)}>Anterior</button>
						<button disabled={hasta >= total} onClick={() => cargar(page + 1)}>Siguiente</button>
					</span>
				</div>
			</div>

			{promo ? <PromoBloqueModal campusId={campusId} filtros={{ ...filtros, etapa: 2 }} onClose={() => setPromo(false)} /> : null}
		</div>
	);
};

export default CarteraView;
